/* Triple 3 product carousels and recently viewed products. */
(() => {
  'use strict';

  const RECENTLY_VIEWED_KEY = 'triple3_recently_viewed';

  function numberFromDataset(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function initializeCarousel(root) {
    if (!root || root.hidden || root.dataset.carouselInitialized === 'true') return;
    if (typeof window.Swiper !== 'function') return;

    const viewport = root.querySelector('[data-carousel-swiper]');
    if (!viewport) return;

    const previous = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    const slidesMobile = numberFromDataset(root.dataset.slidesMobile, 1.2);
    const slidesTablet = numberFromDataset(root.dataset.slidesTablet, 3);
    const slidesDesktop = numberFromDataset(root.dataset.slidesDesktop, 4);
    const spaceBetween = numberFromDataset(root.dataset.spaceBetween, 16);
    const mobileSpaceBetween = numberFromDataset(
      getComputedStyle(document.documentElement).getPropertyValue('--grid-gap-mobile').trim().replace('px', ''),
      12
    );

    root.dataset.carouselInitialized = 'true';
    root._swiper = new window.Swiper(viewport, {
      a11y: {
        enabled: true
      },
      grabCursor: true,
      navigation:
        previous && next
          ? {
              prevEl: previous,
              nextEl: next
            }
          : undefined,
      slidesPerView: slidesMobile,
      spaceBetween: mobileSpaceBetween,
      watchOverflow: true,
      breakpoints: {
        640: {
          slidesPerView: slidesTablet
        },
        768: {
          slidesPerView: slidesTablet,
          spaceBetween
        },
        1024: {
          spaceBetween,
          slidesPerView: slidesDesktop
        }
      }
    });
  }

  function initializeCarousels(scope) {
    scope.querySelectorAll?.('[data-product-carousel]').forEach(initializeCarousel);
  }

  function destroyCarousels(scope) {
    scope.querySelectorAll('[data-product-carousel]').forEach((carousel) => {
      carousel._swiper?.destroy(true, true);
      delete carousel._swiper;
      delete carousel.dataset.carouselInitialized;
    });
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') {
        initializeCarousels(this);
        return;
      }
      this.hidden = false;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 300px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.loading = false;
      destroyCarousels(this);
    }

    async load() {
      if (!this.dataset.url || this.loading) return;
      this.loading = true;
      const controller = new AbortController();
      this.controller = controller;

      try {
        const response = await fetch(this.dataset.url, {
          credentials: 'same-origin',
          signal: controller.signal
        });
        if (!response.ok) {
          throw new Error(`Recommendations request failed (${response.status}).`);
        }

        const documentFragment = new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );
        const recommendations = documentFragment.querySelector(
          'product-recommendations'
        );
        if (controller.signal.aborted || !this.isConnected) return;
        if (!recommendations?.innerHTML.trim()) {
          this.hidden = true;
          this.dataset.loaded = 'true';
          return;
        }
        destroyCarousels(this);
        this.innerHTML = recommendations.innerHTML;
        this.hidden = false;
        this.dataset.loaded = 'true';
        initializeCarousels(this);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hidden = true;
          console.error(error);
        }
      } finally {
        if (this.controller === controller) this.loading = false;
      }
    }
  }

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.controller = new AbortController();
      initializeCarousels(this);
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
      delete this.dataset.initialized;
      destroyCarousels(this);
    }

    readHandles() {
      try {
        const parsed = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
        return Array.isArray(parsed)
          ? [...new Set(parsed.filter((handle) => typeof handle === 'string' && handle))].slice(0, 12)
          : [];
      } catch {
        return [];
      }
    }

    storeCurrentHandle(handles) {
      const currentHandle = this.dataset.currentHandle;
      if (!currentHandle) return;

      const nextHandles = [
        currentHandle,
        ...handles.filter((handle) => handle !== currentHandle)
      ].slice(0, 12);

      try {
        localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(nextHandles));
      } catch {
        // Storage can be unavailable in private or restricted browser contexts.
      }
    }

    async fetchProduct(handle, signal) {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(
          handle
        )}?section_id=${encodeURIComponent(this.dataset.sectionId)}`,
        {
          credentials: 'same-origin',
          signal
        }
      );
      if (!response.ok) return null;

      const documentFragment = new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );
      const template = documentFragment.querySelector(
        'template[data-recently-viewed-template]'
      );
      const slide = template?.content?.firstElementChild;
      if (slide?.dataset.recentProductHandle !== handle) return null;
      return slide.cloneNode(true);
    }

    async load() {
      const controller = this.controller;
      const currentHandle = this.dataset.currentHandle;
      const maxProducts = Math.min(
        8,
        Math.max(1, numberFromDataset(this.dataset.maxProducts, 8))
      );
      const storedHandles = this.readHandles();
      const handlesToRender = storedHandles
        .filter((handle) => handle !== currentHandle)
        .slice(0, maxProducts);

      this.storeCurrentHandle(storedHandles);
      if (!handlesToRender.length) return;

      try {
        const slides = (
          await Promise.all(
            handlesToRender.map((handle) => this.fetchProduct(handle, controller.signal))
          )
        ).filter(Boolean);
        if (controller.signal.aborted || !this.isConnected || !slides.length) return;

        const list = this.querySelector('[data-recently-viewed-list]');
        const content = this.querySelector('[data-recently-viewed-content]');
        const carousel = this.querySelector('[data-product-carousel]');
        const empty = this.querySelector('[data-recently-viewed-empty]');
        if (!list || !content || !carousel || !empty) return;

        destroyCarousels(this);
        list.replaceChildren(...slides);
        empty.hidden = true;
        carousel.hidden = false;
        content.hidden = false;
        this.hidden = false;
        initializeCarousels(this);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }
  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }

  document.addEventListener('shopify:section:unload', (event) => {
    destroyCarousels(event.target);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeCarousels(event.target);
  });
})();
